import { useEffect, useRef, useState } from "react";
import { notePaused, playExclusive } from "../../lib/audio/exclusive-playback";
import { formatElapsed } from "../../lib/audio/format-elapsed";
import { useAuth } from "../../lib/auth/auth-context";
import { createAudioSignedUrl } from "../../lib/captures/fetch";
import { Icon } from "./Icon";
import { InfoTip } from "./InfoTip";

/**
 * Lecteur audio DS (§6.5 — émeraude = écoute) : bouton play 44 px icône-seule,
 * temps courant, barre de progression custom (seek), mini-égaliseur pendant la
 * lecture, `<audio>` caché (jamais de contrôle natif).
 *
 * Gestion interne : l'URL signée n'est tirée qu'au premier clic (puis autoplay),
 * et la lecture est EXCLUSIVE — démarrer ce lecteur met en pause toute autre
 * capture en cours de lecture, toutes surfaces confondues (exclusive-playback).
 *
 * Quand l'utiliser : réécouter l'audio d'une capture (carte de l'accueil, page
 * dédiée). Les états « pas d'audio » (supprimé / jamais existé) restent chez
 * l'appelant : ne rendre `AudioPlayer` que si `audioPath` est présent.
 *
 * `fateExplanation` (optionnel) : l'infobulle « ce qui arrive à cet audio »
 * (DS §6.14), au doigt, à droite des contrôles.
 */
interface AudioPlayerProps {
  /** Chemin de l'objet audio dans le bucket (jamais une URL signée). */
  audioPath: string;
  /** Durée connue de la capture (s) — affichée tant que le média n'est pas chargé. */
  durationS: number;
  /**
   * Sort de cet audio (rétention), en une ou deux phrases : rendu en infobulle
   * au doigt à côté des contrôles (DS §6.14). Le lecteur ne le calcule pas
   * lui-même — le texte dépend de la capture, pas du média (cf.
   * `captureAudioFateExplanation`). Absent → pas d'infobulle.
   */
  fateExplanation?: string | null;
}

export function AudioPlayer({ audioPath, durationS, fateExplanation }: AudioPlayerProps) {
  const { client } = useAuth();
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  // État de lecture/progression alimenté par les events du <audio>.
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (audioUrl && audioRef.current) {
      // Autoplay au chargement de l'URL signée, coordonné « une seule lecture à
      // la fois » (met en pause toute autre capture). playExclusive avale un
      // éventuel rejet d'autoplay (WKWebView).
      playExclusive(audioRef.current);
    }
  }, [audioUrl]);

  async function handleToggle() {
    // Pas encore chargé : on tire l'URL signée, l'effet lance la lecture.
    if (!audioUrl) {
      setLoadingAudio(true);
      setAudioError(null);
      const r = await createAudioSignedUrl(client, audioPath);
      setLoadingAudio(false);
      if (r.ok) setAudioUrl(r.url);
      else setAudioError(r.error);
      return;
    }
    // Déjà chargé : bascule lecture / pause.
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) playExclusive(a);
    else a.pause();
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex min-w-0 items-center gap-2.5">
        <button
          type="button"
          onClick={handleToggle}
          disabled={loadingAudio}
          aria-label={isPlaying ? "Pause" : "Écouter"}
          aria-busy={loadingAudio}
          className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-full bg-emerald text-white transition-transform duration-150 motion-reduce:transition-none hover:-translate-y-px active:scale-95 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange focus-visible:ring-offset-2"
        >
          <Icon name={isPlaying ? "pause" : "play"} size={18} />
        </button>
        <div className="flex min-w-0 flex-1 items-center gap-2 text-small text-muted tabular-nums">
          <span>{formatElapsed(currentTime * 1000)}</span>
          {audioUrl ? (
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.1}
              value={Math.min(currentTime, duration || 0)}
              onChange={(e) => {
                const t = Number(e.currentTarget.value);
                if (audioRef.current) audioRef.current.currentTime = t;
                setCurrentTime(t);
              }}
              aria-label="Position de lecture"
              className="h-1 min-w-0 flex-1 cursor-pointer accent-emerald"
            />
          ) : (
            <span aria-hidden="true" className="h-1 flex-1 rounded-full bg-line" />
          )}
          {isPlaying ? (
            <span className="cap-eq" aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
            </span>
          ) : (
            <span>{formatElapsed((audioUrl && duration ? duration : durationS) * 1000)}</span>
          )}
        </div>
        {/* Le sort de l'audio se lit LÀ OÙ on l'écoute : c'est la seule surface
            commune à toutes les vues d'une capture (carte d'accueil, page
            dédiée). Au doigt, jamais au survol (DS §6.14). */}
        {fateExplanation && <InfoTip label="Ce qui arrive à cet audio">{fateExplanation}</InfoTip>}
        {audioUrl && (
          <audio
            ref={audioRef}
            src={audioUrl}
            className="hidden"
            onPlay={() => setIsPlaying(true)}
            onPause={() => {
              setIsPlaying(false);
              if (audioRef.current) notePaused(audioRef.current);
            }}
            onEnded={() => {
              setIsPlaying(false);
              if (audioRef.current) notePaused(audioRef.current);
            }}
            onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
            onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
          >
            <track kind="captions" />
          </audio>
        )}
      </div>
      {/* DS §6.13 : `audioError` est la phrase rédigée par
          `createAudioSignedUrl`. Elle apparaît après un appui sur ▶ — donc
          annoncée (§7), sinon on touche le bouton, rien ne joue, et on
          n'apprend rien. */}
      {audioError && (
        <p role="alert" className="m-0 text-small text-red-deep">
          {audioError}
        </p>
      )}
    </div>
  );
}
